import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import Loader from 'Components/Loader'
import { DialogModal } from 'Components/UIComponents'
import LayoutContainer from 'Components/LayoutComponent'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { TABLE_CONST } from 'Utils/Constants'
import EditCountryWiseExportReport from './EditCountryWiseExportReport'
import AddCountryWiseExportReport from './AddCountryWiseExportReport'

const CountryWiseExportReport = ({ setLoading, mockCountryWiseExportReport, mockColumns, mockFileName, mockExportReportData }) => {
  const { t } = useTranslation()
  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount

  const [selectedCountry, setSelectedCountry] = useState({})
  const [exportCountries, setExportCountries] = useState([])
  const [columns, setColumns] = useState([])
  const [selectedYear, setSelectedYear] = useState({})
  const [yearList, setYearList] = useState([])
  const [exportReportData, setExportReportData] = useState([])
  const [exportDataByCountry, setExportDataByCountry] = useState([])
  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedRowData, setSelectedRowData] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [fileName, setFileName] = useState('')
  const [year, setYear] = useState('')
  const [errorModal, setErrorModal] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  useEffect(() => {
    fetchExportCountries()

    // testing purpose
    if (mockCountryWiseExportReport) {
      setColumns(mockColumns)
      setFileName(mockFileName)
      setExportReportData(mockExportReportData)
    }
  }, [])

  const fetchReportData = async (fileName) => {
    handleDismiss()
    setLoading(true)
    const retryFunction = () => fetchReportData(fileName)

    try {
      const response = await apiAdapterCoffeeWeb.getDataByReportName({ reportName: fileName })
      const { year } = response.data[0]

      const reportData = response?.data.map((item) => item.report)[0]
      const reportYear = response?.data.map((item) => ({ year: item.year.split(' : ')[1] }))
      const defaultYear = year.split(' : ')[1]

      setYear(year)
      setYearList(reportYear)
      setSelectedYear({ year: defaultYear })
      setExportDataByCountry(response.data)
      if (reportData && reportData?.length > 0) {
        const dynamicColumns = Object.keys(reportData[0]).map((key) => ({
          field: key,
          header: key.charAt(0).toUpperCase() + key.slice(1)
        }))

        dynamicColumns.push({
          field: 'actions',
          header: t('ACTIONS')
        })
        setColumns([...dynamicColumns])

        setExportReportData(reportData)
      }
    } catch {
      handleApiError('NO_DATA_FOUND', retryFunction)
    } finally {
      setLoading(false)
    }
  }

  const fetchExportCountries = async () => {
    handleDismiss()

    setLoading(true)
    const retryFunction = () => fetchExportCountries()

    try {
      const { data: countries } = await apiAdapterCoffeeWeb.getAllExportCountryData()
      const exportCountriesData = countries.filter((item) => item.iscofexportcountry)

      fetchReportData(`${exportCountriesData[0]?.nicename?.toLowerCase()}.json`)

      setFileName(`${exportCountriesData[0]?.nicename?.toLowerCase()}.json`)
      setExportCountries(exportCountriesData)
      setSelectedCountry(exportCountriesData[0])
    } catch {
      handleApiError('FAILED_TO_FETCH_THE_EXPORT_COUNTRY_DATA', retryFunction)
    } finally {
      setLoading(false)
    }
  }

  const deleteReportData = async (rowData) => {
    setLoading(true)
    setErrorModal({ ...errorModal, showModal: false })
    const retryFunction = () => deleteReportData(rowData)

    try {
      const response = await apiAdapterCoffeeWeb.deleteCountryWiseExportReportData({
        fileName: fileName,
        Year: year,
        Month: rowData.month || rowData.MONTH
      })

      const {
        data: { statusCode }
      } = response

      if (statusCode === 200) {
        handleSuccessResponse(response)
      } else {
        handleApiError('FAILED_TO_DELETE_REPORT_DATA', retryFunction)
      }
    } catch (error) {
      handleApiError('FAILED_TO_DELETE_REPORT_DATA', retryFunction)
    } finally {
      setLoading(false)
    }
  }

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  const handleApiError = (errorMessage, onRetry) => {
    setErrorModal({
      ...errorModal,
      showModal: true,
      modalType: 'error',
      message: t(errorMessage),
      onRetry: onRetry,
      onHide: handleDismiss
    })
  }

  const handleSuccessResponse = (response) => {
    const fullReportData = response?.data.returnLst
    const reportData = fullReportData.map((item) => item.report)[0]
    const reportYear = response?.data?.returnLst?.map((item) => ({ year: item.year.split(' : ')[1] }))
    const coffeeReportByYear = fullReportData?.find((report) => report?.year.split(' : ')[1] === reportYear[0]?.year)

    setYearList(reportYear)
    setSelectedYear({ year: reportYear[0]?.year })
    setExportDataByCountry(fullReportData)

    if (reportData && reportData.length > 0) {
      const dynamicColumns = Object.keys(reportData[0]).map((key) => ({
        field: key,
        header: key.charAt(0).toUpperCase() + key.slice(1)
      }))

      dynamicColumns.push({
        field: 'actions',
        header: t('ACTIONS')
      })

      setColumns(dynamicColumns)
      setExportReportData(coffeeReportByYear.report)
    }

    setErrorModal({
      ...errorModal,
      showModal: true,
      modalType: 'success',
      message: t('REPORT_DATA_DELETED_SUCCESSFULLY'),
      onHide: handleDismiss,
      onSuccess: handleDismiss
    })
  }

  const handleReportData = (responseData) => {
    const fullReportData = responseData?.data.returnLst
    const reportData = fullReportData.map((item) => item.report)[0]
    const reportYear = responseData?.data?.returnLst?.map((item) => ({ year: item.year.split(' : ')[1] }))
    const filteredDate = reportYear?.filter((item) => item.year === selectedYear?.year)
    const coffeeReportByYear = fullReportData?.find((report) => report?.year.split(' : ')[1] === selectedYear?.year)

    setYearList(reportYear)
    setSelectedYear({ year: filteredDate[0]?.year })
    setExportDataByCountry(fullReportData)
    if (reportData && reportData?.length > 0) {
      const dynamicColumns = Object.keys(reportData[0]).map((key) => ({
        field: key,
        header: key.charAt(0).toUpperCase() + key.slice(1)
      }))

      dynamicColumns.push({
        field: 'actions',
        header: t('ACTIONS')
      })
      setColumns([...dynamicColumns])

      setExportReportData(coffeeReportByYear.report)
    }
  }

  const handleCountryChange = (event) => {
    const value = event.target.value

    fetchReportData(`${value?.nicename?.toLowerCase()}.json`)
    setFileName(`${value?.nicename?.toLowerCase()}.json`)
    setSelectedCountry(value)
    setExportReportData([])
    setColumns([])
  }

  const handleYearChange = (event) => {
    const value = event.target.value

    setYear(`Year : ${value.year}`)

    setSelectedYear(value)

    const coffeeReportByYear = exportDataByCountry?.find((report) => report?.year.split(' : ')[1] === value?.year)

    if (coffeeReportByYear) {
      setExportReportData(coffeeReportByYear.report)
    }
  }

  const handleDelete = (rowData) => {
    setErrorModal({
      ...errorModal,
      showModal: true,
      modalType: 'info',
      message: t('DELETE_USDA_COFFEE_REPORT'),
      onConfirmation: () => deleteReportData(rowData),
      onHide: handleDismiss
    })
  }

  const handleDismiss = () => {
    setErrorModal({ ...errorModal, showModal: false })
  }

  const handleEdit = (rowData) => {
    setShowEditModal(true)
    setSelectedRowData(rowData)
  }

  const handleAddButtonClick = () => {
    setShowForm(true)
  }

  return (
    <>
      <div className="export-report-container" data-testid="export-report-container">
        <LayoutContainer title={t('COUNTRY_WISE_EXPORT_REPORT')} breadCrumbsTitle={'countryExportReport'} showAddButton={true} handleAddButtonClick={handleAddButtonClick}>
          <div className="dropdown-wrapper">
            <Dropdown value={selectedCountry} options={exportCountries} optionLabel="nicename" onChange={handleCountryChange} placeholder={t('SELECT_COUNTRY')} className="country-dropdown" data-testid="country-dropdown" />
            <Dropdown value={selectedYear} options={yearList} optionLabel="year" onChange={handleYearChange} placeholder={t('SELECT_YEAR')} className="year-dropdown" data-testid="year-dropdown" />
          </div>
          <div className="data-table">
            <DataTable value={exportReportData} paginator={exportReportData?.length} rows={rowCount} pageLinkSize={paginationButtons} columns={columns} emptyMessage={t('FETCHING')}>
              {columns.map((col) => {
                if (col.field === 'actions') {
                  return (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      className="table-action-column"
                      body={(rowData) => (
                        <div className="actions-column">
                          <Button type="button" icon="pi pi-pencil" className="edit-button" tooltip={t('EDIT')} tooltipOptions={{ position: 'top' }} onClick={() => handleEdit(rowData)} data-testid="edit-button" />
                          <Button type="button" icon="pi pi-trash" className="deleted-button" tooltip={t('DELETE')} tooltipOptions={{ position: 'top' }} onClick={() => handleDelete(rowData)} data-testid="delete-button" />
                        </div>
                      )}
                    />
                  )
                }

                return <Column key={col.field} field={col.field} header={col.header} />
              })}
            </DataTable>
          </div>
          <DialogModal {...errorModal} onDismiss={handleDismiss} />
          <AddCountryWiseExportReport columns={columns} showForm={showForm} setShowForm={setShowForm} fileName={fileName} handleReportDataFunction={handleReportData} country={selectedCountry} />
          <EditCountryWiseExportReport showEditModal={showEditModal} setShowEditModal={setShowEditModal} rowData={selectedRowData} country={selectedCountry} fullReportData={exportDataByCountry} activeYear={year} fileName={fileName} handleReportDataFunction={handleReportData} />
        </LayoutContainer>
      </div>
    </>
  )
}

export default Loader(CountryWiseExportReport)
